'use client'

import { useSearchParams } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import { currentUrlParamRefinements, subtypeFilterProperty } from '../services/refinement.service'

export const useRefinementUrl = () => {
  const readOnlySearchParams = useSearchParams()

  const clone = useCallback((): URLSearchParams => {
    return new URLSearchParams(readOnlySearchParams.toString())
  }, [readOnlySearchParams])

  const setRefinement = useCallback(
    (key: string, value: string, distinct = true) => {
      const params = clone()

      if (distinct) {
        params.set(key, value)
      } else {
        // Allow multiple values for the same key.
        const existingValues = params.getAll(key)

        if (!existingValues.includes(value)) {
          params.append(key, value)
        }
      }

      return params
    },
    [clone],
  )

  const deleteRefinement = useCallback(
    (key: string, value?: string, distinct = true) => {
      const params = clone()

      if (distinct) {
        params.delete(key)
      } else {
        if (value) {
          // Still allow other values for the same key.
          const remainingValues = params.getAll(key).filter((v) => v !== value)

          params.delete(key)
          remainingValues.forEach((v) => params.append(key, v))
        } else {
          console.warn('When using distinct, please provide a value we need to remove.')
        }
      }

      return params
    },
    [clone],
  )

  const currentRefinements = useCallback(() => {
    return currentUrlParamRefinements(readOnlySearchParams).filter((refinement) => refinement.property.id != subtypeFilterProperty.id)
  }, [readOnlySearchParams])

  const count = useMemo(() => currentRefinements().flatMap((refinement) => refinement.values).length, [currentRefinements])

  return { readOnlySearchParams, setRefinement, deleteRefinement, currentRefinements, clone, count }
}
